import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelBody, PanelHead } from '../../components/primitives/Panel';
import type { BrowseUrlPatch, BrowseUrlState } from '../hooks/useBrowseUrlState';
import type { BrowseSearchData } from '../hooks/useBrowseSearch';
import { browseMessages as messages } from '../browseMessages';
import { BrowseSearchBox } from './BrowseSearchBox';
import { BrowseTypeSwitch } from './BrowseTypeSwitch';
import { BrowseFilters } from './BrowseFilters';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseSearch.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowseSearchPanelProps {
  state: BrowseUrlState;
  update: (patch: BrowseUrlPatch) => void;
  data: BrowseSearchData | undefined;
  hasQuery: boolean;
  onSubmit: (query: string) => void;
  onPasteLink: () => void;
  onOpenFilterSheet: () => void;
}

/**
 * The search panel (design a1). It stays on top in every state, so the user can always type again
 * (design b). The type switch and the filters show once there is a query.
 */
const BrowseSearchPanelComponent: React.FC<BrowseSearchPanelProps> = ({
  state,
  update,
  data,
  hasQuery,
  onSubmit,
  onPasteLink,
  onOpenFilterSheet,
}) => {
  const intl = useIntl();
  return (
    <Panel as="section">
      <PanelHead
        title={intl.formatMessage(messages.searchTitle)}
        icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
        subtitle={intl.formatMessage(messages.searchSub)}
        right={intl.formatMessage(messages.slashHint)}
      />
      <PanelBody className={styles.panelBody}>
        <BrowseSearchBox
          query={state.query}
          onQueryChange={(query) => update({ query: query.trim() === '' ? undefined : query })}
          onSubmit={onSubmit}
        />
        {hasQuery && (
          <BrowseTypeSwitch
            value={state.type}
            onChange={(type) => update({ type: type === 'all' ? undefined : type })}
            data={data}
          />
        )}
        <BrowseFilters state={state} update={update} onPasteLink={onPasteLink} onOpenFilterSheet={onOpenFilterSheet} />
      </PanelBody>
    </Panel>
  );
};

export const BrowseSearchPanel = TypedMemo(BrowseSearchPanelComponent);
